import getOfferWithInitializedPrices from '@/helpers/productListHelpers/getOfferWithInitializedPrices'
import { normalizeProductListPrices } from '@/helpers/productListHelpers/productListGetters'
import { additionalServicesCopyWithExtraFields } from '@/helpers/additionalServices'
import { ApiManager } from '@/services/http/ApiManager'
import { OFFER_STATUS, DEFAULT_RATE_MODEL_ID } from '@/helpers/constants'
import {
  SET_OFFER,
  SET_OFFER_ITEM,
  SET_OFFER_PRODUCT_SELECTION,
  SET_OFFER_BASEPRICE_ITEM,
  SET_OFFER_PRODUCT_ITEM,
  SET_OFFER_DISCOUNT,
  SET_PACKAGE_ITEM,
  SET_OFFER_DISPERSION,
  SET_PRODUCT_CURRENT_WEIGHT_LIST,
  UPDATE_PRODUCT_BASE_PRICES,
  SET_ALL_DISCOUNTS_OF_PRODUCT,
  RESET_VALIDATED_OFFER_STATUS,
} from './mutations'
import { GET_PRODUCT_ID_LIST, GET_PRODUCT_STD_DISCOUNT_RATES } from './getters'

export const REQUEST_OFFER_DATA = 'REQUEST_OFFER_DATA'
export const UPDATE_OFFER_ITEM = 'UPDATE_OFFER_ITEM'
export const UPDATE_OFFER = 'UPDATE_OFFER'
export const UPDATE_OFFER_PRODUCT_SELECTION = 'UPDATE_OFFER_PRODUCT_SELECTION'
export const UPDATE_OFFER_BASEPRICE_ITEM = 'UPDATE_OFFER_BASEPRICE_ITEM'
export const UPDATE_AND_SEND_OFFER = 'UPDATE_AND_SEND_OFFER'
export const UPDATE_OFFER_PRODUCT_ITEM = 'UPDATE_OFFER_PRODUCT_ITEM'
export const UPDATE_OFFER_DISCOUNT = 'UPDATE_OFFER_DISCOUNT'
export const UPDATE_PACKAGE_ITEM = 'UPDATE_PACKAGE_ITEM'
export const UPDATE_OFFER_DISPERSION = 'UPDATE_OFFER_DISPERSION'
export const SEND_CONTRACT_TO_SIGN = 'SEND_CONTRACT_TO_SIGN'
export const MANUAL_SIGN = 'MANUAL_SIGN'
export const REQUEST_MODELS = 'REQUEST_MODELS'
export const REQUEST_ORGANIZATION = 'REQUEST_ORGANIZATION'
export const SET_OFFER_TARIFF_MODEL = 'SET_OFFER_TARIFF_MODEL'
export const REQUEST_CONTRACT = 'REQUEST_CONTRACT'
export const REQUEST_VALIDATION_STATUS = 'REQUEST_VALIDATION_STATUS'
export const REQUEST_OFFER_LIST = 'REQUEST_OFFER_LIST'
export const CREATE_OFFER = 'CREATE_OFFER'
export const RESET_OFFER = 'RESET_OFFER'
export const REQUEST_CONTRACT_STATUS = 'REQUEST_CONTRACT_STATUS'
export const CANCEL_SIGN = 'CANCEL_SIGN'
export const UPLOAD_MODEL = 'UPLOAD_MODEL'
export const UPDATE_PRODUCT_CURRENT_WEIGHT_LIST = 'UPDATE_PRODUCT_CURRENT_WEIGHT_LIST'
export const RESET_PRODUCTS_OFFER_PRICES = 'RESET_PRODUCTS_OFFER_PRICES'
export const CLONE_OFFER = 'CLONE_OFFER'
export const REQUEST_TRANSLATIONS = 'REQUEST_TRANSLATIONS'
export const CREATE_TRANSLATION = 'CREATE_TRANSLATION'
export const UPDATE_TRANSLATION = 'UPDATE_TRANSLATION'
export const DELETE_TRANSLATION = 'DELETE_TRANSLATION'
export const CONFIRM_EDIT_VALIDATED_OFFER = 'CONFIRM_EDIT_VALIDATED_OFFER'
export const CANCEL_EDIT_VALIDATED_OFFER = 'CANCEL_EDIT_VALIDATED_OFFER'

export default {
  /**
   * Request Offer data by offerId
   * @param context.commit
   * @param {Object} payload
   * @param {string} payload.offerId
   */
  async [REQUEST_OFFER_DATA]({ commit }, payload) {
    try {
      const offer = await ApiManager.requestOfferData(payload.offerId)
      const formattedResp = JSON.parse(JSON.stringify(offer.data))

      if (formattedResp.status !== OFFER_STATUS.CREATED) {
        try {
          const rateModelResp = await ApiManager.requestModel(formattedResp.rateModelId)
          formattedResp.rateModel = JSON.parse(JSON.stringify(rateModelResp.data))
          formattedResp.extraServicesList = additionalServicesCopyWithExtraFields(formattedResp)

          commit('rateModel/set', rateModelResp.data, { root: true })
        } catch {
          formattedResp.rateModel = {}
        }
      } else {
        formattedResp.rateModel = {}
      }

      commit(SET_OFFER, formattedResp)
      return formattedResp
    } catch (error) {
      return parseRequestError(error)
    }
  },

  /**
   * Returns offer validationStatus.
   * @param context.commit
   * @param {Object} payload
   * @param {string} payload.offerId
   */
  async [REQUEST_VALIDATION_STATUS]({ commit }, payload) {
    const response = await ApiManager.requestValidationStatus(payload.offerId)
    const responseData = response.data || null
    commit(SET_OFFER_ITEM, [
      {
        name: 'validationStatus',
        value: responseData.toUpperCase(),
      },
    ])
    return response
  },

  /**
   * Update in db a previously created offer
   * @param context.commit
   * @param {Object} payload
   * @param {string} payload.offerId
   * @param {Object} payload.offerData
   */
  async [UPDATE_OFFER](_, payload) {
    try {
      const normalizedOfferData = {
        ...payload.offerData,
        productList: normalizeProductListPrices(payload.offerData.productList),
      }
      const res = await ApiManager.updateOffer(payload.offerId, normalizedOfferData)
      return res.status
    } catch (error) {
      return parseRequestError(error)
    }
  },

  /**
   * Send offer data to CRM & increase offer version
   * @param context.commit
   * @param {Object} payload
   * @param {string} payload.offerId
   * @param {Object} payload.offerData
   */
  async [UPDATE_AND_SEND_OFFER](_, payload) {
    try {
      const normalizedOfferData = {
        ...payload.offerData,
        productList: normalizeProductListPrices(payload.offerData.productList),
      }
      const res = await ApiManager.updateAndSendOffer(payload.offerId, normalizedOfferData)
      return res
    } catch (error) {
      return parseRequestError(error)
    }
  },

  /**
   * Create new offer
   * @param context.commit
   * @param { Object } payload
   * @param { Object } payload.client
   * @param { Object } payload.comercialAgent
   * @param { string } payload.currencyISO
   * @param { string } payload.offerExternalId
   * @param { string } payload.potentialShipments
   * @param { Object } payload.bankDetails
   * @param { Array } payload.optyServicePoints
   */
  async [CREATE_OFFER](_, payload) {
    try {
      const { objToSend, rateModelId } = payload
      const createOfferPayload = { ...objToSend }
      createOfferPayload.rateModelId = rateModelId || DEFAULT_RATE_MODEL_ID
  
      const response = await ApiManager.createOffer(createOfferPayload)
      return response
    } catch (error) {
      return parseRequestError(error)
    }
  },  

  /**
   * Reset offer to created state
   * @param context.commit
   * @param { Object } payload
   * @param { string } payload.offerId
   * @param { Object } payload.client
   * @param { Object } payload.commercial
   * @param { string } payload.currencyISO
   * @param { string } payload.offerExternalId
   * @param { string } payload.potentialShipments
   * @param { Object } payload.bankDetails
   * @param { Array } payload.optyServicePoints
   */
  async [RESET_OFFER](_, payload) {
    const resetPayload = {
      offerId: payload.offerId,
      client: payload.client,
      comercialAgent: { name: payload.commercial },
      currencyISO: payload.currencyISO,
      offerExternalId: payload.offerExternalId,
      potentialShipments: payload.potentialShipments,
      bankDetails: payload.bankDetails,
      optyServicePoints: payload.optyServicePoints,
      rateModelId: DEFAULT_RATE_MODEL_ID,
    }
    const response = await ApiManager.resetOffer(resetPayload)
    return response
  },

  /**
   * Send contract - set offer status to 'SIGNING'
   * @param context.commit
   * @param { Object } payload
   * @param { string } payload.offerId
   * @param { Object } payload.contract
   */
  async [SEND_CONTRACT_TO_SIGN]({ commit }, payload) {
    try {
      const res = await ApiManager.sendContractToSign(payload.offerId, payload.contract)
      commit(SET_OFFER_ITEM, [{ name: 'status', value: 'SIGNING' }])
      return res
    } catch (error) {
      return parseRequestError(error)
    }
  },

  /**
   * Set the offer as manually signed
   * @param context.commit
   * @param { Object } payload
   * @param { string } payload.offerId
   * @param { Object } payload.contract
   */
  async [MANUAL_SIGN](_, payload) {
    try {
      const res = await ApiManager.manualSign(payload.offerId, payload.contract)
      return res
    } catch (error) {
      return parseRequestError(error)
    }
  },

  /**
   * Cancellation of the signature process (possible only before signature concluded - offer status 'SIGNED')
   * @param context.commit
   * @param { Object } payload
   * @param { string } payload.offerId
   */
  async [CANCEL_SIGN]({ commit }, payload) {
    try {
      const res = await ApiManager.cancelSign(payload.offerId)
      commit(SET_OFFER_ITEM, [{ name: 'status', value: 'EDITED' }])
      return res
    } catch (error) {
      return parseRequestError(error)
    }
  },

  /**
   * Import a new tariff model from the admin dashboard
   * @param context.commit
   * @param { Object } payload
   * @param { Object } payload.rateModel
   */
  async [UPLOAD_MODEL](_, payload) {
    const response = await ApiManager.uploadModel(payload.rateModel)
    return response
  },

  /**
   * Request contract status
   * @param context.commit
   * @param { Object } payload
   * @param { string } payload.offerId
   */
  async [REQUEST_CONTRACT_STATUS]({ commit, state }, payload) {
    try {
      const res = await ApiManager.requestContractStatus(payload.offerId)
      commit('process/setSignStatus', { ...state.offer, contract: { ...res.data } }, { root: true })
      return res
    } catch (error) {
      return parseRequestError(error)
    }
  },

  /**
   * Get the contract from db
   * @param context.commit
   * @param { Object } payload
   * @param { string } payload.offerId
   */
  async [REQUEST_CONTRACT](_, payload) {
    try {
      const res = await ApiManager.requestContract(payload.offerId)
      return res.data
    } catch (error) {
      return parseRequestError(error)
    }
  },

  /**
   * Get offer list to show in the admin dashboard
   * @param context.commit
   */
  async [REQUEST_OFFER_LIST]() {
    try {
      const response = await ApiManager.requestOfferList()
      return response
    } catch (error) {
      return parseRequestError(error)
    }
  },

   

  /**
   * Get information about the available tariff models
   * @param context.commit
   */
    async [REQUEST_MODELS](_, organization = null) {
      try {
        const res = await ApiManager.requestModels(organization);
        return res;
      } catch (error) {
        return parseRequestError(error);
      }
    },
  

    /**
   * Get the organization of the current offer
   * @param {String} rateModelId - The rate model ID of the offer
   */
  async [REQUEST_ORGANIZATION](_, rateModelId) {
    try {
      console.log(rateModelId)
      const res = await ApiManager.requestOrganization(rateModelId);
      return res;
    } catch (error) {
      return parseRequestError(error);
    }
  },


  /**
   * Set the tariff model for a previously created offer (rateModelId) and update offer with the corresponding tariff pricing info
   * @param context.commit
   * @param {Object} payload
   * @param {string} payload.offerId
   * @param {number} payload.model - tariff model id
   * @param {Object} payload.offer - offer data
   */
  async [SET_OFFER_TARIFF_MODEL]({ commit, dispatch, state }, payload) {
    try {
      const tariff = await ApiManager.setRateModel(payload.offerId, payload.model, payload.offer)
      //filter faulty  productList> pricingRules > pivotProductRules
      const formattedResp = getFilteredOffer(JSON.parse(JSON.stringify(tariff.data)))

      if (formattedResp.status !== OFFER_STATUS.CREATED) {
        try {
          const rateModelResp = await ApiManager.requestModel(payload.model)
          formattedResp.rateModel = JSON.parse(JSON.stringify(rateModelResp.data))
          formattedResp.extraServicesList = additionalServicesCopyWithExtraFields(formattedResp)

          commit('rateModel/set', rateModelResp.data, { root: true })
        } catch {
          formattedResp.rateModel = {}
        }
      } else {
        formattedResp.rateModel = {}
      }

      //Initialize products discounts & basePrices
      //TODO: move this logic to backend
      const offerWithInitializedProductPrices = getOfferWithInitializedPrices(formattedResp)

      if (state.offer?.legalCity)
        offerWithInitializedProductPrices.legalCity = state.offer.legalCity;
        
      commit(SET_OFFER, offerWithInitializedProductPrices)
      dispatch('UPDATE_OFFER', {
        offerId: payload.offerId,
        offerData: offerWithInitializedProductPrices,
      })
      return offerWithInitializedProductPrices
    } catch (error) {
      return parseRequestError(error)
    }
  },

  /**
   * @param context
   * @param {Object} payload
   * @param {string} payload.offerId - Target offerId
   * @param {string} payload.sourceOfferId - Source offerId
   */
  async [CLONE_OFFER](_, payload) {
    try {
      const response = await ApiManager.cloneOffer(payload.offerId, payload.sourceOfferId)
      return response
    } catch (error) {
      return { errorCode: error.response.status }
    }
  },

  /**
   * Get the available translations
   */
  async [REQUEST_TRANSLATIONS](_, payload) {
    try {
      const res = await ApiManager.requestTranslations(payload)
      return res
    } catch {
      return 'invalid'
    }
  },

  /**
   * Add a new translation
   * @param context
   * @param { Object } payload
   * @param { String } payload.key
   * @param { String } payload.value
   */
  async [CREATE_TRANSLATION](_, payload) {
    try {
      const res = await ApiManager.createTranslation(payload)
      return res
    } catch {
      return 'invalid'
    }
  },

  /**
   * Edit a translation
   * @param context
   * @param { Object } payload
   * @param { String } payload.key
   * @param { String } payload.value
   */
  async [UPDATE_TRANSLATION](_, payload) {
    try {
      const res = await ApiManager.updateTranslation(payload.key, payload)
      return res
    } catch {
      return 'invalid'
    }
  },

  /**
   * Delete a translation
   * @param context
   * @param { Object } payload
   * @param { String } payload.key
   */
  async [DELETE_TRANSLATION](_, payload) {
    try {
      const res = await ApiManager.deleteTranslation(payload.key)
      return res
    } catch {
      return 'invalid'
    }
  },

  // Below here, actions not calling the ApiManager

  async [UPDATE_OFFER_ITEM]({ commit }, payload) {
    commit(SET_OFFER_ITEM, [
      {
        name: payload.name,
        value: payload.value,
      },
    ])
  },

  async [UPDATE_OFFER_PRODUCT_ITEM]({ commit }, payload) {
    commit(SET_OFFER_PRODUCT_ITEM, [
      {
        name: payload.name,
        value: payload.value,
      },
    ])
  },

  async [UPDATE_OFFER_PRODUCT_SELECTION]({ commit }, payload) {
    commit(SET_OFFER_PRODUCT_SELECTION, payload)
  },

  async [UPDATE_OFFER_BASEPRICE_ITEM]({ commit }, payload) {
    commit(SET_OFFER_BASEPRICE_ITEM, payload)
  },

  async [UPDATE_PACKAGE_ITEM]({ commit }, payload) {
    commit(SET_PACKAGE_ITEM, payload)
  },

  async [UPDATE_OFFER_DISCOUNT]({ commit }, payload) {
    commit(SET_OFFER_DISCOUNT, payload)
  },

  async [UPDATE_OFFER_DISPERSION]({ commit }, payload) {
    commit(SET_OFFER_DISPERSION, payload)
  },

  /**
   * @param context.commit
   * @param {Object} payload
   * @param {string} payload.productId
   * @param {Array} payload.weightList - array of weights (numbers)
   */
  async [UPDATE_PRODUCT_CURRENT_WEIGHT_LIST]({ commit, state }, payload) {
    const { productId, weightList } = payload
    const productIndex = state.offer.productList.findIndex(product => product.id === productId)
    commit(SET_PRODUCT_CURRENT_WEIGHT_LIST, { productIndex, weightList })
    commit(UPDATE_PRODUCT_BASE_PRICES, { productIndex, weightList })
  },

  /**
   * @param context
   */
  async [RESET_PRODUCTS_OFFER_PRICES]({ commit, getters }) {
    const productIdList = getters[GET_PRODUCT_ID_LIST]
    for (const productId of productIdList) {
      const discountRates = getters[GET_PRODUCT_STD_DISCOUNT_RATES](productId)
      commit(SET_ALL_DISCOUNTS_OF_PRODUCT, {
        productId,
        discountRates,
      })
    }
  },

  /**
   *
   * @param context
   */
  async [CANCEL_EDIT_VALIDATED_OFFER]({ commit, dispatch, state }) {
    commit('process/setShowEditValidatedOfferDialog', false, { root: true })
    const currentStep = state.offer.step
    await dispatch('REQUEST_OFFER_DATA', {
      offerId: state.offer.offerId,
    })
    commit(SET_OFFER_ITEM, [{ name: 'step', value: currentStep }])
  },

  /**
   *
   * @param context
   */
  [CONFIRM_EDIT_VALIDATED_OFFER]({ commit }) {
    commit('process/setShowEditValidatedOfferDialog', false, { root: true })
    commit(RESET_VALIDATED_OFFER_STATUS)
  },
}

const filterPivotProductRules = arr => arr.filter(el => el.pivotProductId !== '')

const getFilteredProduct = obj => {
  const filteredPivotProductRules = filterPivotProductRules(obj.pricingRules.pivotProductRules)
  const pricingRules = { ...obj.pricingRules, pivotProductRules: filteredPivotProductRules }
  return { ...obj, pricingRules }
}

const getFilteredProductList = arr => arr.map(product => getFilteredProduct(product))

const getFilteredOffer = obj => ({ ...obj, productList: getFilteredProductList(obj.productList) })

const parseRequestError = requestError => {
  let error = ''

  if ('response' in requestError && 'data' in requestError.response) {
    error = JSON.stringify(requestError.response.data, null, 1)
  } else {
    error = JSON.stringify(requestError, null, 1)
  }

  if (error === '{}') {
    const errorString = requestError.toString()

    error = errorString !== '[object Object]' ? errorString : requestError
  }

  return { error }
}
